/**
 * `/confirm_account` is where the account confirmation email lands. It confirms the code, says
 * how that went in a snackbar, and moves on: into the app for a signed-in visitor, otherwise to
 * the login page.
 */

import { redirect } from '@tanstack/react-router';
import { getApiClientErrorBody, getApiClientErrorDetail, isApiClientErrorStatus4xx } from '@/services/api/errors';

export type ConfirmAccountSnackbar = {
  variant: 'default' | 'critical';
  id: string;
  message: string;
};

const INVALID_CODE: ConfirmAccountSnackbar = {
  variant: 'critical',
  id: 'confirmAccount.invalidConfirmationCode',
  message: 'Account confirmation code is incorrect.',
};

const CONFIRM_FAILED: ConfirmAccountSnackbar = {
  variant: 'critical',
  id: 'confirmAccount.error',
  message: "Your account couldn't be confirmed. Open the link from your email again.",
};

const CONFIRMED: ConfirmAccountSnackbar = {
  variant: 'default',
  id: 'confirmAccount.success',
  message: 'Account confirmed.',
};

function alreadyConfirmed(isAuthenticated: boolean): ConfirmAccountSnackbar {
  return {
    variant: 'critical',
    id: 'confirmAccount.accountAlreadyConfirmed',
    message: isAuthenticated ? 'Account already confirmed.' : 'Account already confirmed. Log in to continue.',
  };
}

function failureSnackbar(error: unknown, isAuthenticated: boolean): ConfirmAccountSnackbar {
  // The API answers a code that was already used with a 400 carrying this detail.
  if (getApiClientErrorDetail(getApiClientErrorBody(error)) === 'account already confirmed') {
    return alreadyConfirmed(isAuthenticated);
  }
  // Only the API can say the code is wrong. A dropped connection or a server error says nothing
  // about it, and the same link may well work on a second try.
  return isApiClientErrorStatus4xx(error) ? INVALID_CODE : CONFIRM_FAILED;
}

type ConfirmAccountOptions = {
  /** The code exactly as the email link wrote it, or `null` when the link has none. */
  confirmationCode: string | null;
  isAuthenticated: boolean;
  confirm: (confirmationCode: string) => Promise<void>;
};

/** Confirms the code and returns the snackbar that reports the result. */
export async function confirmAccountFromLink({
  confirmationCode,
  isAuthenticated,
  confirm,
}: ConfirmAccountOptions): Promise<ConfirmAccountSnackbar> {
  if (confirmationCode == null || confirmationCode === '') {
    return INVALID_CODE;
  }
  try {
    await confirm(confirmationCode);
    return CONFIRMED;
  } catch (error) {
    return failureSnackbar(error, isAuthenticated);
  }
}

/** The redirect that leaves the confirmation page. */
export function confirmAccountExit(isAuthenticated: boolean) {
  return isAuthenticated
    ? redirect({ to: '/', replace: true })
    : redirect({ to: '/login/$', params: { _splat: '' }, replace: true });
}
